import React from "react";
import { motion } from "framer-motion";
import { Home, Activity, Shield, Brain, FileCheck, Users } from "lucide-react";

export function HowElevitaWorksSection() {
  // Define the steps for the main Elevita workflow
  const workflowSteps = [
    {
      icon: Home,
      title: "Initial Assessment",
      description: "Our natural conversation interface captures your symptoms and concerns with empathy.",
      color: "#14b8a6" // Luminescent Teal
    },
    {
      icon: Brain,
      title: "AI Analysis",
      description: "Dynamic AI logic intelligently adapts to your unique situation, processing complex information.",
      color: "#10B981" // Emerald Green
    },
    {
      icon: Activity, // Represents activity/monitoring
      title: "Risk Assessment",
      description: "Clear visualisation of your condition's severity, guiding immediate next steps.",
      color: "#F59E0B" // Orange
    },
    {
      icon: Shield,
      title: "Automatic Escalation",
      description: "Proactive alerts to '000' or healthcare facilities when urgent intervention is required.",
      color: "#EF4444" // Red
    },
    {
      icon: FileCheck,
      title: "Clinical Summary",
      description: "Comprehensive, AI-generated reports for seamless handover to healthcare providers.",
      color: "#8B5CF6" // Violet
    },
    {
      icon: Users,
      title: "Continuous Support",
      description: "Ongoing recovery tracking and care coordination for your sustained wellbeing.",
      color: "#14b8a6" // Teal 500
    }
  ];

  // --- SVG Workflow Loop Calculation ---
  const centerX = 600; // Center X of the SVG viewBox (1200 / 2)
  const centerY = 200; // Center Y of the SVG viewBox (400 / 2)
  const radiusX = 450; // Horizontal radius for the ellipse
  const radiusY = 150; // Vertical radius for the ellipse (makes it an ellipse, not a circle)

  // Calculate positions for nodes around the ellipse
  const getNodePosition = (index: number, totalSteps: number) => {
    const angle = (index / totalSteps) * 2 * Math.PI - Math.PI / 2; // Start from top, go clockwise
    const x = centerX + radiusX * Math.cos(angle);
    const y = centerY + radiusY * Math.sin(angle);
    return { x, y };
  };

  // Generate points for the user journey indicator to follow the elliptical path
  const userJourneyPoints = [];
  const numPoints = 100; // More points for smoother animation
  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI - Math.PI / 2;
    userJourneyPoints.push(`${centerX + radiusX * Math.cos(angle)},${centerY + radiusY * Math.sin(angle)}`);
  }

  // Extract Cx and Cy arrays for Framer Motion animation
  const userJourneyCx = userJourneyPoints.map(point => parseFloat(point.split(',')[0]));
  const userJourneyCy = userJourneyPoints.map(point => parseFloat(point.split(',')[1]));


  return (
    <section id="how-it-works" className="py-20 relative z-10 bg-white dark:bg-black text-gray-100">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Space Mono'] text-black dark:text-white">
            How We're Building This: <span className="text-teal-600 dark:text-teal-400">Your Healthcare Journey</span>
          </h2>
          <p className="text-xl text-black dark:text-white max-w-3xl mx-auto font-['Inter']">
            Dynamic AI and human-centred design create a seamless and supportive healthcare experience, adapting to your unique needs, all in.   </p>
        </motion.div>

        {/* Interactive workflow diagram - Now a loop */}
        <div className="max-w-6xl mx-auto mb-16 p-2 text-black dark:text-white">
          <div className="bg-white dark:bg-black p-8 rounded-2xl border border-teal-600 dark:border-teal-400 shadow-xl relative overflow-hidden">
            {/* Background glowing grid/lines */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                        <path d="M 10 0 L 0 0 0 10" fill="none" stroke="teal" strokeWidth="0.2" />
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                    <circle cx="50" cy="50" r="30" fill="none" stroke="teal" strokeWidth="0.5" opacity="0.3">
                        <animate attributeName="r" values="30;35;30" dur="4s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="teal" strokeWidth="0.5" opacity="0.2">
                        <animate attributeName="r" values="40;48;40" dur="5s" repeatCount="indefinite" />
                    </circle>
                </svg>
            </div>

            <svg viewBox="0 0 1200 400" className="w-full h-64 relative z-10">
              <defs>
                  {/* Radial gradient for node inner glow */}
                  <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                      <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                      <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                  </radialGradient>
                  {/* Linear gradient for path flow */}
                  <linearGradient id="pathFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#14b8a6" />
                      <stop offset="100%" stopColor="#0d9488" />
                  </linearGradient>
                  {/* Filter for glow effect */}
                  <filter id="glow">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                      <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                      </feMerge>
                  </filter>
              </defs>
              <motion.g
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
              >
                {/* Main elliptical flow path */}
                <motion.path
                  d={`M ${centerX + radiusX},${centerY} A ${radiusX},${radiusY} 0 1 0 ${centerX - radiusX},${centerY} A ${radiusX},${radiusY} 0 1 0 ${centerX + radiusX},${centerY}`}
                  stroke="url(#pathFlowGradient)" /* Use gradient for stroke */
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray="20,10"
                  animate={{ strokeDashoffset: [0, -1000] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  filter="url(#glow)" /* Apply glow filter */
                />

                {/* Step nodes */}
                {workflowSteps.map((step, index) => {
                  const { x, y } = getNodePosition(index, workflowSteps.length);

                  return (
                    <motion.g
                      key={step.title}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.2 }}
                      className="cursor-pointer group"
                      style={{ color: step.color }} /* Set color for currentColor in gradient */
                      filter="url(#glow)" /* Apply glow filter to group */
                    >
                      {/* Outer glow circle (pulsing) */}
                      <motion.circle
                        cx={x} cy={y} r="45"
                        fill="none"
                        stroke="currentColor" /* Use currentColor */
                        strokeWidth="2"
                        initial={{ opacity: 0.4, scale: 0.8 }}
                        animate={{ opacity: [0.4, 0.6, 0.4], scale: [0.8, 1, 0.8] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
                      />
                      {/* Step background (darker fill) */}
                      <circle cx={x} cy={y} r="40" className="fill-white dark:fill-black" stroke="currentColor" strokeWidth="1" />
                      {/* Inner subtle glow from gradient */}
                      <circle cx={x} cy={y} r="38" fill="url(#nodeGradient)" opacity="0.3" />

                      {/* Icon */}
                      <foreignObject x={x - 12} y={y - 12} width="24" height="24">
                        <div className="flex items-center justify-center w-full h-full">
                          <step.icon size={24} style={{ color: 'currentColor' }} /> {/* Use currentColor */}
                        </div>
                      </foreignObject>

                      {/* Step title */}
                      <text x={x} y={y + 50} textAnchor="middle" fill="currentColor" fontSize="14" fontWeight="bold" className="font-['Inter']">
                        {step.title}
                      </text>

                       {/* Description on hover (using SVG text for simplicity, could be HTML tooltip) */}
                       <text x={x} y={y + 70} textAnchor="middle" fill="#B0B0B0" fontSize="10" className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-['Inter']">
                           {step.description.split(' ')[0]}
                       </text>
                    </motion.g>
                  );
                })}

                {/* User journey indicator */}
                <motion.circle
                  r="10"
                  fill="#14b8a6"
                  filter="url(#glow)" /* Apply glow filter */
                  // Animate cx and cy directly using the generated points
                  animate={{
                    cx: userJourneyCx,
                    cy: userJourneyCy
                  }}
                  transition={{
                    duration: 6, // Duration for one full loop
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <animate
                    attributeName="r"
                    values="10;14;10"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="1;0.7;1"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </motion.circle>
              </motion.g>
            </svg>

            {/* Step descriptions (moved below SVG for better layout on smaller screens) */}
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 mt-8 bg-white dark:bg-black">
              {workflowSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  className="text-center p-4 rounded-lg border border-teal bg-white dark:bg-black bg-opacity-30"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 1.5 + index * 0.1 }}
                >
                  <div
                    className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center border-2 bg-white dark:bg-black"
                    style={{ backgroundColor: `${step.color}20`, borderColor: step.color }}
                  >
                    <step.icon size={28} style={{ color: step.color }} />
                  </div>
                  <h4 className="font-semibold mb-2 text-lg font-['Inter'] text-black dark:text-white" style={{ color: step.color }}>
                    {step.title}
                  </h4>
                  <p className="text-sm text-black dark:text-white font-['Inter']">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Assessment Display Example */}
        <motion.div
          className="max-w-4xl mx-auto my-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center text-teal-600 dark:text-teal-400 font-['Space Mono']">
            Risk Assessment Display: <span className="text-black dark:text-white">Clarity in Urgency</span>
          </h3>

          <div className="bg-white/80 dark:bg-black/80 p-8 rounded-xl border border-gray-700 shadow-xl">
            <div className="space-y-6">
              {[
                { level: "Low Risk", color: "#10B981", width: "25%", description: "Condition stable, monitor as advised." },
                { level: "Moderate Risk", color: "#F59E0B", width: "50%", description: "Requires attention, follow up with GP soon." },
                { level: "Urgent Risk", color: "#EF4444", width: "75%", description: "Immediate medical attention recommended." },
                { level: "High Risk", color: "#DC2626", width: "90%", description: "Emergency services (000) or hospital visit advised." }
              ].map((risk, index) => (
                <motion.div
                  key={risk.level}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm md:text-base font-medium font-['Inter']" style={{ color: risk.color }}>
                      {risk.level}
                    </span>
                    <span className="text-xs md:text-sm text-gray-800 dark:text-gray-200 font-['Inter']">{risk.width}</span>
                  </div>
                  <div className="w-full bg-gray-800 dark:bg-gray-200 rounded-full h-3">
                    <motion.div
                      className="h-3 rounded-full"
                      style={{ backgroundColor: risk.color }}
                      initial={{ width: 0 }}
                      whileInView={{ width: risk.width }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                    />
                  </div>
                  <p className="text-xs text-black dark:text-white mt-1 font-['Inter']">{risk.description}</p>
                </motion.div>
              ))}
            </div>

            <p className="text-center text-black dark:text-white mt-6 italic font-['Inter']">
              "Clear visualisation of your condition's severity for better decision-making."
            </p>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
